import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Box, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { AppDispatch, RootState } from './store';
import { store } from './store';
import theme from './theme';
import Login from './pages/Login';
import Dashboard from './pages/dashboard/DashboardPage';
import Licenses from './pages/licenses';
import Devices from './pages/devices';
import UserListPage from './pages/users/UserListPage';
import UserCreatePage from './pages/users/UserCreatePage';
import MyPage from './pages/MyPage';
import MyApplication from './pages/MyApplication';
import { OwnerSettingsPage } from './pages/OwnerSettingsPage';
import { ApplicationListPage } from './pages/ApplicationListPage';
import { ApplicationCreateFromProductPage } from './pages/ApplicationCreateFromProductPage';
import LicenseCreatePage from './pages/licenses/LicenseCreatePage';
import MyLicensesPage from './pages/MyLicensesPage';
import MyDevicesPage from './pages/MyDevicesPage';
import ErrorSnackbar from './components/ErrorSnackbar';
import PrivateRoute from './components/auth/PrivateRoute';
import { verifyAuth } from './store/slices/authSlice';
import { SessionTimeoutAlert } from './components/SessionTimeoutAlert';
import Layout from './components/Layout';

const queryClient = new QueryClient();

interface PrivateRouteProps {
  element: React.ReactElement;
}

const PrivateRouteWrapper: React.FC<PrivateRouteProps> = ({ element }) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isLoggingOut = useSelector((state: RootState) => state.auth.isLoggingOut);
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);
  const [isVerifying, setIsVerifying] = React.useState(true);

  useEffect(() => {
    // 初回マウント時に必ず認証チェックを実行
    const verifyAuthentication = async () => {
      // ログアウト中の場合はverifyAuthをスキップ
      const hasToken = localStorage.getItem('accessToken');
      if (!isLoggingOut && hasToken) {
        console.log('🔍 認証状態を確認中...');
        await dispatch(verifyAuth());
      } else {
        console.log('🔒 認証チェックをスキップ（ログアウト中またはトークンなし）');
      }
      setIsVerifying(false);
    };
    verifyAuthentication();
  }, [dispatch, isLoggingOut]); // isLoggingOutも依存関係に追加

  // ログアウト中
  if (isLoggingOut) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 認証チェック中
  if (isLoading || isVerifying) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <CircularProgress />
    </Box>;
  }

  // 認証されていない場合はログインページへ
  if (!isAuthenticated) {
    console.log('認証されていません。ログインページへリダイレクト');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return element;
};

const AppRoutes: React.FC = () => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isLoggingOut = useSelector((state: RootState) => state.auth.isLoggingOut);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated && !isLoggingOut ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Login />
          )
        }
      />
      <Route element={<Layout />}>
        <Route path="/dashboard" element={<PrivateRouteWrapper element={<Dashboard />} />} />
        <Route path="/licenses" element={<PrivateRouteWrapper element={<Licenses />} />} />
        <Route path="/licenses/create" element={<PrivateRouteWrapper element={<LicenseCreatePage />} />} />
        <Route path="/devices" element={<PrivateRouteWrapper element={<Devices />} />} />
        <Route path="/my-application" element={<PrivateRouteWrapper element={<MyApplication />} />} />
        <Route path="/applications" element={<PrivateRouteWrapper element={<ApplicationListPage />} />} />
        <Route path="/applications/create-from-product" element={<PrivateRouteWrapper element={<ApplicationCreateFromProductPage />} />} />
        <Route path="/applications/:id" element={<Navigate to="/applications" replace />} />
        <Route path="/owner-settings" element={<PrivateRouteWrapper element={<OwnerSettingsPage />} />} />
        <Route path="/users" element={<PrivateRouteWrapper element={<UserListPage />} />} />
        <Route path="/users/create" element={<PrivateRouteWrapper element={<UserCreatePage />} />} />
        <Route path="/mypage" element={<PrivateRouteWrapper element={<MyPage />} />} />
        {/* ユーザーロール専用ページ */}
        <Route path="/my-licenses" element={<PrivateRouteWrapper element={<MyLicensesPage />} />} />
        <Route path="/my-devices" element={<PrivateRouteWrapper element={<MyDevicesPage />} />} />
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Route>
    </Routes>
  );
};

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <CssBaseline />
          <Router>
            <SessionTimeoutAlert />
            <AppRoutes />
          </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
